import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Col, Row } from 'react-bootstrap';
import AppSearch from './AppSearch';
import { useSearchParams } from 'react-router-dom';
import Select, { DefaultOptionType, SelectProps } from 'antd/es/select';
import { useAuth } from '../../contexts/auth';
import { cloneDeep, debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Checkbox, CheckboxChangeEvent, Flex, Pagination, TreeSelect, TreeSelectProps } from 'antd';
import classNames from 'classnames';
import { EventEmitter } from '../../utils/EventEmitter';

type Props = {
  title: string;
  total?: number;
  actions?: React.ReactNode;
  extra?: React.ReactNode;
  filters: Filters;
  disabled?: boolean;
}

function AppBrowsingToolbar({ title, total, extra, actions, filters, disabled }: Props) {
  const { loggedUser } = useAuth();
  const [editingFilters, setEditingFilters] = useState<Filters>();
  // states for pagination props
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDesc, setSortDesc] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    setEditingFilters(curr => {
      const newState = { ...filters };
      for (const key in curr)
        if (newState[key as FilterKey]) newState[key as FilterKey]!.value = curr[key as FilterKey]!.value;
      return newState;
    });
  }, [filters]);

  const selectFilters = useMemo(() => {
    return editingFilters
      ? Object.entries(editingFilters).filter(([_, filter]) => filter.select).map(([key, filter]) => ({ ...filter, key: key as FilterKey }))
      : [];
  }, [editingFilters]);

  const checkboxFilters = useMemo(() => {
    return editingFilters
      ? Object.entries(editingFilters).filter(([_, filter]) => filter.checkbox).map(([key, filter]) => ({ ...filter, key: key as FilterKey }))
      : [];
  }, [editingFilters]);

  const treeSelectFilters = useMemo(() => {
    return editingFilters
      ? Object.entries(editingFilters).filter(([_, filter]) => filter.treeSelect).map(([key, filter]) => ({ ...filter, key: key as FilterKey }))
      : [];
  }, [editingFilters]);

  const initOptions = useCallback(() => {
    setEditingFilters(current => {
      const newState = { ...current! };
      for (const [key, value] of searchParams.entries())
        if (newState[key as FilterKey] !== undefined) {
          if (newState[key as FilterKey]!.selectProps?.mode) newState[key as FilterKey]!.value = value.split(",");
          else
            newState[key as FilterKey]!.value = newState[key as FilterKey]!.type === 'number'
              ? +value
              : newState[key as FilterKey]!.type === 'boolean'
                ? value === "true"
                : value;
        }
      return newState as Filters;
    });
    if (searchParams.has('page')) setCurrentPage(+(searchParams.get('page') || 1));
    if (searchParams.has('size')) setPageSize(+(searchParams.get('size') || 20));
    if (searchParams.has('desc')) setSortDesc(searchParams.get('desc') === "true");
  }, [searchParams]);

  useEffect(() => {
    initOptions();
    // eslint-disable-next-line
  }, []);

  const debounceSearchParamsUpdate = useMemo(() => debounce(setSearchParams, 500), [setSearchParams]);

  const handleFilterChange = useCallback((filter: (FilterType & { key: FilterKey })) => (value: any) => {
    setCurrentPage(1);
    setEditingFilters(curr => {
      const newState = cloneDeep(curr!);
      if (typeof value === "object" && value.length === 0) newState[filter.key]!.value = undefined;
      else newState[filter.key]!.value = value;
      return newState;
    });
    debounceSearchParamsUpdate((previous: any) => {
      const newParams = new URLSearchParams(previous);
      if (!value || value === '' || value.length === 0) newParams.delete(filter.key);
      else newParams.set(filter.key, value);
      newParams.set("page", "1");
      return newParams;
    });
  }, [debounceSearchParamsUpdate]);

  const handleCheckboxFilterChange = useCallback((filter: (FilterType & { key: FilterKey })) => (evt: CheckboxChangeEvent) => {
    const value = evt.target.checked;
    handleFilterChange(filter)(value);
  }, [handleFilterChange])

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    setSearchParams((previous) => {
      const newParams = new URLSearchParams(previous);
      newParams.set("page", `${page}`);
      return newParams;
    });
  }, [setSearchParams]);

  const handlePageSizeChange = useCallback((page: number, size: number) => {
    handlePageChange(page);
    setPageSize(size);
    setSearchParams((previous) => {
      const newParams = new URLSearchParams(previous);
      newParams.set("size", `${size}`);
      newParams.set("page", `${page}`);
      return newParams;
    });
  }, [handlePageChange, setSearchParams]);

  const handleReset = useCallback(() => {
    const filtersParams: Record<string, string> = {};
    for (const [key, filter] of Object.entries(editingFilters || {})) {
      if (filter.value) {
        if (key === 'owned') filtersParams.createdBy = loggedUser!.sub;
        else filtersParams[filter.query] = `${filter.value}`;
      }
    }
    const params = {
      ...filtersParams,
      page: `${currentPage}`,
      size: `${pageSize}`,
      sort: "id",
      desc: `${sortDesc}`,
    };
    // sending an event instead of triggering onReset
    EventEmitter.dispatch('browsing:paramsChanged', params);
  }, [editingFilters, currentPage, pageSize, sortDesc, loggedUser]);

  const debounceReset = useMemo(() => debounce(handleReset, 500), [handleReset]);

  // debounce onReset on every editingFilters change
  useEffect(debounceReset, [debounceReset, editingFilters]);

  // cleanup debounced handlers
  useEffect(() => {
    return () => {
      debounceReset.cancel();
    }
  }, [debounceReset]);

  useEffect(() => {
    return () => {
      debounceSearchParamsUpdate.cancel();
    }
    // eslint-disable-next-line
  }, []);

  const changeSortMode = useCallback(() => {
    const newSort = !sortDesc;
    setSortDesc(newSort);
    setSearchParams((previous) => {
      const newParams = new URLSearchParams(previous);
      newParams.set("desc", `${newSort}`);
      return newParams;
    });
  }, [sortDesc, setSearchParams]);

  if (!editingFilters) return <></>;

  return (
    <>
      <Row className="mb-2 align-items-center">
        <Col>
          <Flex align="center">
            <h4 className="mb-0 me-3">{title}</h4>
            {extra}
          </Flex>
        </Col>

        {actions && <Col md="auto" className="text-end">{actions}</Col>}
      </Row>
      <Row className="gx-2">
        {!!editingFilters.search && (
          <Col className="mb-2" md={3}>
            <AppSearch id="search-input" placeholder={editingFilters.search.placeholder} value={editingFilters.search.value as string} onChange={handleFilterChange({ ...filters.search!, key: 'search' })} />
          </Col>
        )}
        {selectFilters.map(filter => (
          <Col key={filter.key} className="mb-2" md={filter.col || 2}>
            <Select
              {...filter.selectProps}
              allowClear
              loading={!filter.options}
              placeholder={filter.placeholder}
              onChange={handleFilterChange(filter)}
              options={filter.customOptions ? undefined : filter.options}
              value={filter.value}
              className="w-100"
            >
              {filter.customOptions && filter.options?.map(filter.renderOption!)}
            </Select>
          </Col>
        ))}
        {treeSelectFilters.map(filter => (
          <Col key={filter.key} className="mb-2" md={filter.col || 2}>
            <TreeSelect
              {...filter.treeSelectProps}
              allowClear
              loading={!filter.treeSelectProps?.treeData}
              placeholder={filter.placeholder}
              onChange={handleFilterChange(filter)}
              value={filter.value}
              className="w-100"
              loadData={filter.loadData}
              treeNodeFilterProp="title"
            />
          </Col>
        ))}
      </Row>
      <Row>
        <Col md="auto">
          <Flex align="center">
            {checkboxFilters.map((filter, index) => (
              filter.render
                ? filter.render(filter, handleFilterChange(filter))
                : (
                  <Checkbox
                    key={filter.key}
                    checked={filter.value as boolean}
                    onChange={handleCheckboxFilterChange(filter)}
                    className={classNames({ 'ms-2': index > 0 })}
                  >
                    {filter.label}
                  </Checkbox>
                )
            ))}
          </Flex>
        </Col>
        <Col>
          <Pagination
            disabled={disabled}
            total={total || 0}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
            pageSize={pageSize}
            current={currentPage}
            onChange={handlePageChange}
            onShowSizeChange={handlePageSizeChange}
            align="center"
          />
        </Col>
        <Col md={2}>
          <Button size="sm" variant="light" className="w-100 border" onClick={changeSortMode}>
            <FontAwesomeIcon icon={sortDesc ? solid('sort-numeric-down-alt') : solid('sort-numeric-down')} />
            <span className="ms-2">Sort: <b>{sortDesc ? "descending" : "ascending"}</b></span>
          </Button>
        </Col>
      </Row>
    </>
  )
}

export default memo(AppBrowsingToolbar);

interface FilterType {
  // key: FilterKey;
  query: string;
  value?: string | number | boolean | string[];
  type?: 'string' | 'number' | 'boolean';
  options?: DefaultOptionType[];
  customOptions?: boolean;
  renderOption?: (value: DefaultOptionType, index: number, array?: any[]) => React.ReactNode;
  placeholder?: string;
  label?: React.ReactNode;
  select?: boolean;
  treeSelect?: boolean;
  checkbox?: boolean;
  selectProps?: SelectProps;
  treeSelectProps?: TreeSelectProps;
  loadData?: (nodeData: any) => Promise<void>;
  render?: (filter: FilterType, onChange?: (value: any) => void) => React.ReactNode;
  col?: number;
}
type FilterKey = 'search' | 'owned' | 'categories' | 'accessLevel' | 'defaultOnly' | 'activeSchedules' | 'status' | 'created' | 'tags' | 'templatesOnly' | 'deleted' | 'datasourceType' | 'liveMode';
export type Filters = Partial<Record<FilterKey, FilterType>>;